import { MutableRefObject, useLayoutEffect, useState } from 'react';

interface ElementSize {
  width: number;
  height: number;
}

/**
 * Hook that tracks the size of an HTML element.
 * @template T - The type of the HTML element.
 * @param {MutableRefObject<T>} ref - The mutable ref object that references the HTML element.
 * @returns {ElementSize} - The size of the HTML element.
 */
const useElementResize = <T extends HTMLElement = HTMLDivElement>(ref: MutableRefObject<T>): ElementSize => {
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const element = ref.current;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;

      const entry = entries[0];

      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    resizeObserver.observe(element);

    return () => resizeObserver.unobserve(element);
  }, [ref]);

  return size;
};

export default useElementResize;
